import {
  type FocusEvent,
  type KeyboardEvent,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import type { SignupAddressValues } from "./signup-validation.ts";

type GoogleAddressComponent = {
  longText?: string;
  shortText?: string;
  types: string[];
};

type GoogleSessionToken = Record<never, never>;

type GooglePlace = {
  addressComponents?: GoogleAddressComponent[];
  formattedAddress?: string;
  fetchFields: (options: { fields: string[] }) => Promise<void>;
};

type GooglePlacePrediction = {
  placeId: string;
  mainText?: { text: string };
  secondaryText?: { text: string };
  text: { text: string };
  toPlace: () => GooglePlace;
};

type GooglePlacesLibrary = {
  AutocompleteSessionToken: new () => GoogleSessionToken;
  AutocompleteSuggestion: {
    fetchAutocompleteSuggestions: (options: {
      input: string;
      includedPrimaryTypes: string[];
      includedRegionCodes: string[];
      region: string;
      sessionToken: GoogleSessionToken;
    }) => Promise<{
      suggestions: { placePrediction?: GooglePlacePrediction }[];
    }>;
  };
};

type GoogleMapsWindow = Window & {
  google?: {
    maps?: {
      __lockerverseSignupReady?: () => void;
      importLibrary?: (libraryName: string) => Promise<GooglePlacesLibrary>;
    };
  };
};

type AddressSuggestion = {
  id: string;
  label: string;
  secondaryLabel: string;
  prediction: GooglePlacePrediction;
};

const GOOGLE_MAPS_SCRIPT_ID = "lockerverse-signup-google-places";
const GOOGLE_ADDRESS_TYPES = [
  "premise",
  "subpremise",
  "street_address",
  "route",
];
let googlePlacesLoader: Promise<GooglePlacesLibrary> | null = null;

function installGoogleMaps(apiKey: string) {
  const googleWindow: GoogleMapsWindow = window;
  if (googleWindow.google?.maps?.importLibrary) {
    return;
  }
  googleWindow.google ||= {};
  const googleObject = googleWindow.google;
  googleObject.maps ||= {};
  const mapsObject = googleObject.maps;
  let scriptPromise: Promise<void> | null = null;
  mapsObject.importLibrary = (libraryName: string) => {
    scriptPromise ||= new Promise((resolve, reject) => {
      const existingScript = document.getElementById(GOOGLE_MAPS_SCRIPT_ID);
      mapsObject.__lockerverseSignupReady = resolve;
      if (existingScript) {
        existingScript.addEventListener(
          "error",
          () => reject(new Error("Unable to load Google Places.")),
          { once: true }
        );
        return;
      }
      const script = document.createElement("script");
      const parameters = new URLSearchParams({
        callback: "google.maps.__lockerverseSignupReady",
        key: apiKey,
        libraries: libraryName,
        v: "weekly",
      });
      script.async = true;
      script.id = GOOGLE_MAPS_SCRIPT_ID;
      script.src = `https://maps.googleapis.com/maps/api/js?${parameters}`;
      script.onerror = () => reject(new Error("Unable to load Google Places."));
      document.head.append(script);
    });
    return scriptPromise
      .then(() => {
        const importLibrary = googleWindow.google?.maps?.importLibrary;
        if (!importLibrary) {
          throw new Error("Unable to load Google Places.");
        }
        return importLibrary(libraryName);
      })
      .catch((error) => {
        scriptPromise = null;
        document.getElementById(GOOGLE_MAPS_SCRIPT_ID)?.remove();
        throw error;
      });
  };
}

function loadGooglePlaces(apiKey: string) {
  googlePlacesLoader ||= (async () => {
    try {
      installGoogleMaps(apiKey);
      const googleWindow: GoogleMapsWindow = window;
      const importLibrary = googleWindow.google?.maps?.importLibrary;
      if (!importLibrary) {
        throw new Error("Unable to load Google Places.");
      }
      return await importLibrary("places");
    } catch (error) {
      googlePlacesLoader = null;
      throw error;
    }
  })();
  return googlePlacesLoader;
}

function getAddressPart(
  components: GoogleAddressComponent[],
  type: string,
  key: "longText" | "shortText" = "longText"
) {
  return (
    components.find((component) => component.types.includes(type))?.[key] ?? ""
  );
}

function parseAddress(current: SignupAddressValues, place: GooglePlace) {
  const components = place.addressComponents ?? [];
  const street = [
    getAddressPart(components, "street_number"),
    getAddressPart(components, "route"),
  ]
    .filter(Boolean)
    .join(" ");
  return {
    ...current,
    city:
      getAddressPart(components, "locality") ||
      getAddressPart(components, "postal_town") ||
      getAddressPart(components, "sublocality_level_1"),
    country: getAddressPart(components, "country", "shortText") || "US",
    line1: street || place.formattedAddress?.split(",")[0] || "",
    line2: getAddressPart(components, "subpremise"),
    postalCode: [
      getAddressPart(components, "postal_code"),
      getAddressPart(components, "postal_code_suffix"),
    ]
      .filter(Boolean)
      .join("-"),
    state: getAddressPart(
      components,
      "administrative_area_level_1",
      "shortText"
    ),
  } satisfies SignupAddressValues;
}

export function SignupAddressField({
  apiKey,
  errorId,
  invalid,
  onChange,
  required,
  value,
}: {
  apiKey: string | undefined;
  errorId: string | undefined;
  invalid: boolean;
  onChange: (address: SignupAddressValues) => void;
  required: boolean;
  value: SignupAddressValues;
}) {
  const id = useId();
  const listboxId = `${id}-suggestions`;
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1);
  const [focused, setFocused] = useState(false);
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const detailRequestGeneration = useRef(0);
  const searchGeneration = useRef(0);
  const sessionToken = useRef<GoogleSessionToken | null>(null);

  useEffect(
    () => () => {
      detailRequestGeneration.current += 1;
    },
    []
  );

  useEffect(() => {
    const generation = searchGeneration.current + 1;
    searchGeneration.current = generation;
    const query = value.line1.trim();
    setActiveSuggestionIndex(-1);
    setLoading(false);
    if (!(apiKey && focused && query.length >= 3)) {
      setSuggestions([]);
      return;
    }
    const timeout = window.setTimeout(async () => {
      if (generation !== searchGeneration.current) {
        return;
      }
      setLoading(true);
      try {
        const { AutocompleteSessionToken, AutocompleteSuggestion } =
          await loadGooglePlaces(apiKey);
        sessionToken.current ??= new AutocompleteSessionToken();
        const response =
          await AutocompleteSuggestion.fetchAutocompleteSuggestions({
            includedPrimaryTypes: GOOGLE_ADDRESS_TYPES,
            includedRegionCodes: ["US"],
            input: query,
            region: "US",
            sessionToken: sessionToken.current,
          });
        if (generation === searchGeneration.current) {
          setSuggestions(
            response.suggestions.flatMap(({ placePrediction }) =>
              placePrediction
                ? [
                    {
                      id: placePrediction.placeId,
                      label:
                        placePrediction.mainText?.text ??
                        placePrediction.text.text,
                      prediction: placePrediction,
                      secondaryLabel: placePrediction.secondaryText?.text ?? "",
                    },
                  ]
                : []
            )
          );
        }
      } catch {
        if (generation === searchGeneration.current) {
          setSuggestions([]);
        }
      } finally {
        if (generation === searchGeneration.current) {
          setLoading(false);
        }
      }
    }, 250);
    return () => {
      window.clearTimeout(timeout);
    };
  }, [apiKey, focused, value.line1]);

  const update = (patch: Partial<SignupAddressValues>) => {
    detailRequestGeneration.current += 1;
    onChange({ ...value, ...patch });
  };

  const selectSuggestion = async (suggestion: AddressSuggestion) => {
    const generation = detailRequestGeneration.current + 1;
    detailRequestGeneration.current = generation;
    const place = suggestion.prediction.toPlace();
    try {
      await place.fetchFields({
        fields: ["addressComponents", "formattedAddress"],
      });
      if (generation !== detailRequestGeneration.current) {
        return;
      }
      onChange(parseAddress(value, place));
      sessionToken.current = null;
      setSuggestions([]);
      setActiveSuggestionIndex(-1);
      setFocused(false);
    } catch {
      if (generation !== detailRequestGeneration.current) {
        return;
      }
      setSuggestions([]);
      setActiveSuggestionIndex(-1);
    }
  };

  const handleAddressKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Escape") {
      event.preventDefault();
      setSuggestions([]);
      setActiveSuggestionIndex(-1);
      return;
    }
    if (suggestions.length === 0) {
      return;
    }
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveSuggestionIndex((current) =>
        current < suggestions.length - 1 ? current + 1 : 0
      );
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveSuggestionIndex((current) =>
        current > 0 ? current - 1 : suggestions.length - 1
      );
      return;
    }
    const suggestion = suggestions[activeSuggestionIndex];
    if (event.key === "Enter" && suggestion) {
      event.preventDefault();
      selectSuggestion(suggestion).catch(() => undefined);
    }
  };

  const handleAddressBlur = (event: FocusEvent<HTMLElement>) => {
    const search = event.currentTarget.closest(
      ".lockerverse-signup__address-search"
    );
    if (search?.contains(event.relatedTarget)) {
      return;
    }
    window.setTimeout(() => setFocused(false), 120);
  };

  return (
    <fieldset
      aria-describedby={invalid ? errorId : undefined}
      aria-invalid={invalid ? "true" : undefined}
      className="lockerverse-signup__address"
    >
      <legend>Address</legend>
      <label htmlFor={`${id}-line1`}>
        <span>Street address</span>
        <div className="lockerverse-signup__address-search">
          <input
            aria-activedescendant={
              activeSuggestionIndex >= 0
                ? `${id}-suggestion-${activeSuggestionIndex}`
                : undefined
            }
            aria-autocomplete="list"
            aria-controls={listboxId}
            aria-describedby={invalid ? errorId : undefined}
            aria-expanded={focused && suggestions.length > 0}
            aria-invalid={invalid ? "true" : undefined}
            autoComplete="street-address"
            id={`${id}-line1`}
            name="address.line1"
            onBlur={handleAddressBlur}
            onChange={(event) => update({ line1: event.currentTarget.value })}
            onFocus={() => setFocused(true)}
            onKeyDown={handleAddressKeyDown}
            required={required}
            role="combobox"
            value={value.line1}
          />
          {loading ? (
            <span className="lockerverse-signup__address-loading">
              Searching…
            </span>
          ) : null}
          {suggestions.length > 0 ? (
            <div
              className="lockerverse-signup__address-suggestions"
              id={listboxId}
              role="listbox"
            >
              {suggestions.map((suggestion, index) => (
                <button
                  aria-selected={activeSuggestionIndex === index}
                  id={`${id}-suggestion-${index}`}
                  key={suggestion.id}
                  onBlur={handleAddressBlur}
                  onClick={(event) => {
                    // Keyboard activation fires click without a prior mouse down.
                    if (event.detail === 0) {
                      selectSuggestion(suggestion).catch(() => undefined);
                    }
                  }}
                  onMouseDown={(event) => {
                    event.preventDefault();
                    selectSuggestion(suggestion).catch(() => undefined);
                  }}
                  role="option"
                  type="button"
                >
                  <strong>{suggestion.label}</strong>
                  {suggestion.secondaryLabel ? (
                    <span>{suggestion.secondaryLabel}</span>
                  ) : null}
                </button>
              ))}
            </div>
          ) : null}
        </div>
      </label>
      <label htmlFor={`${id}-line2`}>
        <span>Apartment, suite, or unit</span>
        <input
          autoComplete="address-line2"
          id={`${id}-line2`}
          onChange={(event) => update({ line2: event.currentTarget.value })}
          placeholder="Optional"
          value={value.line2 ?? ""}
        />
      </label>
      <div className="lockerverse-signup__address-row">
        <label htmlFor={`${id}-city`}>
          <span>City</span>
          <input
            autoComplete="address-level2"
            id={`${id}-city`}
            onChange={(event) => update({ city: event.currentTarget.value })}
            required={required}
            value={value.city}
          />
        </label>
        <label htmlFor={`${id}-state`}>
          <span>State</span>
          <input
            autoComplete="address-level1"
            id={`${id}-state`}
            onChange={(event) => update({ state: event.currentTarget.value })}
            required={required}
            value={value.state}
          />
        </label>
      </div>
      <div className="lockerverse-signup__address-row">
        <label htmlFor={`${id}-postal`}>
          <span>Postal code</span>
          <input
            autoComplete="postal-code"
            id={`${id}-postal`}
            onChange={(event) =>
              update({ postalCode: event.currentTarget.value })
            }
            required={required}
            value={value.postalCode}
          />
        </label>
        <label htmlFor={`${id}-country`}>
          <span>Country</span>
          <input
            autoComplete="country"
            id={`${id}-country`}
            maxLength={2}
            onChange={(event) =>
              update({ country: event.currentTarget.value.toUpperCase() })
            }
            required={required}
            value={value.country}
          />
        </label>
      </div>
    </fieldset>
  );
}
